import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useDocFormFiller } from "../../hooks/useDocFormFiller";
import { toast } from "react-toastify";
import { Button, Paper } from "@mui/material";
import DynamicForm, { isFieldCompleted } from "../../components/DynamicForm";
import DocumentEditor from "../../components/documentEditor/documentEditor";
import { makeStyles } from "@material-ui/core/styles";
import Loading from "Components/loading";
import { ToastContainer } from "react-toastify";
import { fillDocForm, updateSignature	 } from "Services/docForm.service";
import { useSignatureForms } from "DocPlace/DocForms/hooks/useSignatureForms";
import { useFormCollaboration } from "../../hooks/useFormCollaboration";
import { getSignerDisplayNameFromStorage } from "../../utils/initials";

const useStyles = makeStyles((theme) => ({
	root: {
		flex: 1,
		minHeight: 0,
		height: "100%",
		maxHeight: "100%",
		width: "100%",
		maxWidth: "100%",
		padding: theme.spacing(0.5, 2, 1),
		display: "flex",
		flexDirection: "column",
		backgroundColor: "#E4E9EF",
		boxSizing: "border-box",
		overflow: "hidden",
		[theme.breakpoints.down("md")]: {
			flex: "0 0 auto",
			height: "auto",
			maxHeight: "none",
			overflow: "visible",
			padding: theme.spacing(0.5, 1, 2),
		},
	},
	headerContainer: {
		color: "#006ac6",
		flexShrink: 0,
		marginBottom: theme.spacing(0.5),
		padding: 0,
		width: "100%",
	},
	headerTitle: {
		fontWeight: 700,
		fontSize: "1.375rem",
		lineHeight: 1.3,
		letterSpacing: "-0.02em",
		color: "#006ac6",
		margin: 0,
		[theme.breakpoints.down("sm")]: {
			fontSize: "1.125rem",
		},
	},
	headerSubtitle: {
		fontWeight: 400,
		fontSize: "16px",
		color: "#006ac6",
	},
	container: {
		flex: 1,
		minHeight: 0,
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
		gap: theme.spacing(2),
		overflow: "hidden",
		width: "100%",
		maxWidth: "100%",
		[theme.breakpoints.down("md")]: {
			flexDirection: "column",
			alignItems: "stretch",
			flex: "0 0 auto",
			minHeight: "auto",
			overflow: "visible",
			gap: theme.spacing(1.5),
		},
	},
	pdfContainer: {
		flex: "7 1 0",
		alignSelf: "stretch",
		minWidth: 0,
		minHeight: 0,
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		[theme.breakpoints.down("md")]: {
			flex: "none",
			width: "100%",
			maxWidth: "100%",
			minHeight: 200,
			// Scroll the whole PDF card here (reliable touch scrolling on iOS/Android)
			maxHeight: "min(52vh, 420px)",
			order: 2,
			overflowX: "auto",
			overflowY: "auto",
			WebkitOverflowScrolling: "touch",
			overscrollBehavior: "contain",
			touchAction: "pan-x pan-y pinch-zoom",
		},
	},
	pdfPaper: {
		flex: 1,
		minHeight: 0,
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		borderRadius: 12,
		backgroundColor: "#fff",
		boxShadow:
			"0 10px 40px rgba(0, 38, 86, 0.12), 0 2px 10px rgba(0, 0, 0, 0.08)",
		border: "1px solid rgba(0, 0, 0, 0.06)",
		[theme.breakpoints.down("md")]: {
			borderRadius: 10,
			minHeight: "auto",
			maxHeight: "none",
			flex: "0 0 auto",
			overflow: "visible",
		},
	},
	formContainer: {
		flex: "3 1 0",
		minWidth: 0,
		maxHeight: "100%",
		overflow: "auto",
		alignSelf: "flex-start",
		flexShrink: 0,
		padding: theme.spacing(2.5, 3, 3),
		backgroundColor: "#fff",
		borderRadius: 12,
		display: "flex",
		flexDirection: "column",
		boxShadow:
			"0 12px 40px rgba(0, 38, 86, 0.12), 0 4px 14px rgba(0, 0, 0, 0.06)",
		border: "1px solid rgba(255, 255, 255, 0.9)",
		[theme.breakpoints.down("md")]: {
			order: 1,
			width: "100%",
			maxWidth: "100%",
			flex: "none",
			alignSelf: "stretch",
			maxHeight: "none",
			padding: theme.spacing(2),
			borderRadius: 10,
		},
	},
	buttonContainer: {
		display: "flex",
		justifyContent: "flex-end",
		backgroundColor: "white",
		marginTop: theme.spacing(3),
		marginBottom: 0,
		paddingBottom: theme.spacing(0.5),
		[theme.breakpoints.down("md")]: {
			marginTop: theme.spacing(2),
		},
	},
	rejectButton: {
		backgroundColor: "#fff",
		color: "#000",
		"&:hover": {
			backgroundColor: "#fff",
		},
		width: "100%",
		padding: "8px 16px",
		borderRadius: "10px",
	},
	completeButton: {
		backgroundColor: "#006ac6",
		color: "white",
		"&:hover": {
			backgroundColor: "#006ac6",
		},
		width: "100%",
		padding: "8px 16px",
		borderRadius: "10px",
	},
	formTitle: {
		fontWeight: 700,
		fontSize: "1.25rem",
		color: "#111827",
		marginBottom: theme.spacing(0.5),
		[theme.breakpoints.down("sm")]: {
			fontSize: "1.1rem",
		},
	},
	formSubtitle: {
		fontWeight: 400,
		fontSize: "0.875rem",
		color: "#6b7280",
		marginBottom: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			fontSize: "0.8125rem",
		},
	},
	divider: {	
		width: "100%",
		height: "1px",
		backgroundColor: "#006ac6",
		marginBottom: theme.spacing(1),
	},
	formContent: {
		flexShrink: 0,
	},
}));

function Header() {
	const classes = useStyles();
	return (
		<div className={classes.headerContainer}>
			<h1 className={classes.headerTitle}>Review and Complete</h1>
		</div>
	);
}

function DocFormFiller() {
	const { id, idRequest } = useParams();
	const navigate = useNavigate();
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const [loadingMessage, setLoadingMessage] = useState("");
	const { getDocFormFillerData, fields, urlPdf, values, setValue, flowId, isLoading: isLoadingForm, error: loadError } = useDocFormFiller();

	const { isConnected, sessionId, error: wsError, sendFieldUpdate, sendCheckFlowStatus } = useFormCollaboration(
		idRequest,
		null,
		flowId
	);

	const { signatureImage, getSignatureList } = useSignatureForms();

	// State and ref for scroll synchronization
	const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
	const scrollToFieldRef = useRef<((fieldId: string) => void) | null>(null);

	const handleFieldFocus = useCallback((fieldId: string) => {
		setActiveFieldId(fieldId);
		if (scrollToFieldRef.current) {
			scrollToFieldRef.current(fieldId);
		}
	}, []);

	const handleFieldInView = useCallback((fieldId: string) => {
		setActiveFieldId(fieldId);
		if (scrollToFieldRef.current) {
			scrollToFieldRef.current(fieldId);
		}
	}, []);

	useEffect(() => {
		if (id) {
			getDocFormFillerData(id,idRequest);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	useEffect(() => {
		if (loadError) {
			toast.error("Error loading form. Please try again.");
		}
	}, [loadError]);

	useEffect(() => {
		if (wsError) {
			console.error('Collaboration error:', wsError);
		}
	}, [wsError]);

	useEffect(() => {
		if (isConnected && sessionId) {
			console.log('Collaboration active - Session:', sessionId);
		}
	}, [isConnected, sessionId]);

	const handleReject = async () => {
		navigate("/docForms");
	};

	// Gate submit on the same rules as the sidebar progress bar (API often omits fieldRequired).
	const canSubmitForm = useMemo(() => {
		if (!values.length) return false;
		return values.every(isFieldCompleted);
	}, [values]);

	const validateRequiredFields = () => {
		const incomplete = values.filter((field) => !isFieldCompleted(field));
		if (incomplete.length > 0) {
			const fieldNames = incomplete.map((field: { label: string }) => field.label).join(", ");
			toast.error(`Please complete all fields: ${fieldNames}`);
			return false;
		}
		return true;
	};

	const handleComplete = async () => {
		// Validar campos requeridos antes de enviar
		if (!validateRequiredFields()) {
			return;
		}

		setLoading(true);
		setLoadingMessage("Filling form...");
		try {
			/*
			 values:[
				{
					fieldId: "1",
					fieldValue: "value1",
					fieldType: "text",
				}
			 ]
			 */
			const valuesToSend = values.map((value: {id: string, value: string, fieldType: string}) => ({
				fieldId: value.id,
				fieldValue: value.value,
				fieldType: value.fieldType,
			}));
			await fillDocForm(idRequest, valuesToSend);
			// Notify WS only after fill succeeded so backend sees completed state
			if (sendCheckFlowStatus) {
				sendCheckFlowStatus();
			}
			setLoadingMessage("Form filled successfully");
			navigate("/docForms");
			toast.success("Form filled successfully");
		} catch (error) {
			console.error('Error filling doc form:', error);
			if (
				typeof error === "object" &&
				error !== null &&
				"response" in error &&
				typeof (error as any).response === "object" &&
				(error as any).response !== null &&
				"data" in (error as any).response &&
				typeof (error as any).response.data === "object" &&
				(error as any).response.data !== null &&
				"detail" in (error as any).response.data
			) {
				toast.warning((error as any).response.data.detail);
			} else {
				toast.warning("Error filling form");
			}
		} finally {
			setLoading(false);
		}
	};

	const handleFieldChange = (fieldId: string, value: string, fieldType?: string) => {
		setValue(fieldId, value);

		if (isConnected && sendFieldUpdate) {
			sendFieldUpdate(fieldId, value);
		}
	};

	const handleSaveSignature = async (signatureData: string) => {
		// Aquí puedes implementar la lógica para guardar la firma en el perfil del usuario
		// Por ejemplo, llamar a un servicio API
		toast.success("Signature saved to profile successfully");
		try {
			const response = await updateSignature(signatureData);
			if (response && response.success === true) {
				// Reload signature image after successful update
				await getSignatureList();
			}
		} catch (error) {
			console.error('Error saving signature:', error);
			toast.error("Error saving signature");
		}
	};

	return (
		<div className={classes.root}>
			<ToastContainer autoClose={3000} />
			<Loading
				show={isLoadingForm || loading}
				text={isLoadingForm ? "Loading form..." : loadingMessage}
			/>

			<Header />
			<div className={classes.container}>
				<div className={classes.pdfContainer}>
					<Paper elevation={0} className={classes.pdfPaper}>
						{urlPdf && fields.length > 0 && (
							<DocumentEditor
								fileUrl={urlPdf}
								fields={fields}
								fieldValues={values}
								activeFieldId={activeFieldId}
								scrollToFieldRef={scrollToFieldRef}
							/>
						)}
					</Paper>
				</div>
				<div className={classes.formContainer}>
						<div>
							<h2 className={classes.formTitle}>Form</h2>
							<p className={classes.formSubtitle}>Complete the form below to complete the request</p>
						</div>
						<div className={classes.divider}></div>
						<div className={classes.formContent}>
							<DynamicForm
								values={values}
								onChange={handleFieldChange}
								signatureImage={signatureImage}
								onSaveSignature={handleSaveSignature}
								onFocus={handleFieldFocus}
								onFieldInView={handleFieldInView}
								activeFieldId={activeFieldId}
								signerDisplayName={getSignerDisplayNameFromStorage()}
							/>
						</div>
						<div className={classes.buttonContainer}>
							<Button
								variant="contained"
								className={classes.completeButton}
								onClick={handleComplete}
								size="large"
								color="primary"
								disabled={!canSubmitForm || isLoadingForm || loading}
								title={!canSubmitForm && values.length > 0 ? "Complete all form fields to submit" : undefined}
							>
								Complete & Submit
							</Button>
						</div>
				</div>
			</div>
		</div>
	);
}

export default DocFormFiller;
